import {
  OrderGetResponseType,
  SupplierRelationshipPaymentDueCalculateFromType,
} from "@sellar-io/api-v2/types";
import { Alert } from "../../../components/alert/Alert";
import { BadgeWhite } from "../../../components/badge/Badge";
import { ConsolidatedBanner } from "../../../components/consolidatedBanner/ConsolidateBanner";
import { useBuyerRelationshipList } from "../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { OrderContent } from "./OrderContent";
import { useSellarCreditPaymentStatus } from "../../../components/newOrderSummary/useSellarCreditPaymentStatus";

export const BuyerOrderContent = ({
  order,
}: {
  order: OrderGetResponseType;
}) => {
  const buyerRelationshipsQuery = useBuyerRelationshipList({
    limit: 1,
    query: [
      {
        type: "supplierId",
        modifier: "EQUALS",
        value: order.SupplierId,
      },
    ],
  });

  const acceptedPaymentTermsRequest =
    order.SupplierRetailerConnectionPaymentTermsRequest?.status === "accepted"
      ? order.SupplierRetailerConnectionPaymentTermsRequest
      : null;
  const rejectedPaymentTermsRequest =
    order.SupplierRetailerConnectionPaymentTermsRequest?.status === "rejected"
      ? order.SupplierRetailerConnectionPaymentTermsRequest
      : null;
  const supplierName = order.Supplier.Business.name;
  const { isProcessed } = useSellarCreditPaymentStatus({ orderId: order.id });
  const hasNoPayment = !isProcessed;
  const showSellarCreditSupplierInitiatedBanner =
    order.paymentType === "sellarCredit" &&
    hasNoPayment &&
    order.initiator === "supplier" &&
    !order.cancelledAt;
  const creditTermsText = (
    paymentDueCalculatedFrom: SupplierRelationshipPaymentDueCalculateFromType,
  ) => {
    if (paymentDueCalculatedFrom === "endOfMonth") {
      return "end of current month";
    }
    if (paymentDueCalculatedFrom === "endOfFollowingMonth") {
      return "end of following month";
    }

    return "invoice issue date";
  };
  return (
    <>
      <div className="p-2">
        {acceptedPaymentTermsRequest && (
          <Alert
            type={"success"}
            message={`${supplierName} have approved your request for ${acceptedPaymentTermsRequest.paymentDue} day(s) credit from ${creditTermsText(acceptedPaymentTermsRequest.paymentDueCalculateFrom)}. Your terms have been applied to this order, as well as any future orders with ${supplierName}`}
            title={"Credit request approved"}
          />
        )}
        {rejectedPaymentTermsRequest && (
          <Alert
            type={"information"}
            message={`${supplierName} have not approved your request for ${rejectedPaymentTermsRequest.paymentDue} day(s) credit from ${creditTermsText(rejectedPaymentTermsRequest.paymentDueCalculateFrom)} and your order has been placed as a pro-forma order. If you don’t think this is correct, you can send them a message.`}
            title={"Pro-forma terms applied"}
          />
        )}
        {showSellarCreditSupplierInitiatedBanner && (
          <ConsolidatedBanner
            title={"Order requires buyer confirmation"}
            description={
              <p>
                {order.Supplier.Business.name} have created a Sellar Credit
                order on your behalf. Confirm the order request to add it to
                your Sellar Credit tab.
              </p>
            }
            badge={
              <BadgeWhite
                icon={"CLOCK_OUTLINE"}
                className={"border border-zinc-300"}
              >
                Awaiting confirmation
              </BadgeWhite>
            }
          />
        )}
      </div>
      <OrderContent
        order={order}
        connection={
          buyerRelationshipsQuery.data?.pages[0]?.[0]?.Relationship
            .BuyerRelationshipDetails ?? undefined
        }
        isLoading={buyerRelationshipsQuery.isLoading}
      />
    </>
  );
};
